import { useState, useContext, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api/api";
import logoBranca from "../../assets/logo-transparente.png";
import styles from "./Login.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_ROUTES = {
  superadmin: "/selecionar-cliente",
  admin:      "/inicial",
  vendedor:   "/venda",
  estoque:    "/estoque",
};

const LOCKOUT_KEY        = "fluxoeasy_lockout";
const ATTEMPTS_KEY       = "fluxoeasy_attempts";
const MAX_ATTEMPTS       = 5;
const MIN_SUBMIT_MS      = 1500;   // formulário precisa ficar aberto ao menos 1,5s
const LOCKOUT_DURATIONS  = [30, 120, 300, 600]; // segundos: 30s → 2min → 5min → 10min

// ─── Helpers ──────────────────────────────────────────────────────────────────

const showLoginError = (message) =>
  Swal.fire({ icon: "error", title: "Erro ao entrar", text: message });

const showLoginSuccess = () =>
  Swal.fire({
    icon: "success",
    title: "Login realizado!",
    timer: 1200,
    showConfirmButton: false,
  });

const showLockoutWarning = (seconds) =>
  Swal.fire({
    icon: "warning",
    title: "Muitas tentativas",
    text: `Aguarde ${formatCountdown(seconds)} antes de tentar novamente.`,
    confirmButtonText: "Entendido",
  });

const formatCountdown = (seconds) => {
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m} min`;
  }
  return `${seconds}s`;
};

// ─── Lockout logic ────────────────────────────────────────────────────────────

const getLockoutState = () => {
  try {
    const raw = localStorage.getItem(LOCKOUT_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw);
    if (Date.now() < state.until) return state;
    localStorage.removeItem(LOCKOUT_KEY);
    return null;
  } catch {
    return null;
  }
};

const getAttempts = () => {
  try {
    return parseInt(localStorage.getItem(ATTEMPTS_KEY) || "0", 10);
  } catch {
    return 0;
  }
};

const recordFailedAttempt = () => {
  const attempts = getAttempts() + 1;
  localStorage.setItem(ATTEMPTS_KEY, String(attempts));

  if (attempts >= MAX_ATTEMPTS) {
    // Duração cresce conforme lockouts acumulados
    const lockoutCount = parseInt(localStorage.getItem("fluxoeasy_lockout_count") || "0", 10);
    const duration     = LOCKOUT_DURATIONS[Math.min(lockoutCount, LOCKOUT_DURATIONS.length - 1)];
    const until        = Date.now() + duration * 1000;

    localStorage.setItem(LOCKOUT_KEY, JSON.stringify({ until, duration }));
    localStorage.setItem("fluxoeasy_lockout_count", String(lockoutCount + 1));
    localStorage.setItem(ATTEMPTS_KEY, "0");

    return { locked: true, duration };
  }

  return { locked: false, remaining: MAX_ATTEMPTS - attempts };
};

const clearAttempts = () => {
  localStorage.removeItem(ATTEMPTS_KEY);
  // Não limpa o lockout_count — mantém histórico para penalizar reincidentes
};

// ─── Bot detection helpers ────────────────────────────────────────────────────

const detectAutomation = () => {
  // Checa webdriver (Selenium/Puppeteer/Playwright sem stealth)
  if (navigator.webdriver) return true;

  // Checa propriedades que bots frequentemente esqueceram de imitar
  if (
    typeof navigator.languages === "undefined" ||
    navigator.languages.length === 0
  ) return true;

  // Checa se plugins array está vazio (headless Chrome)
  if (
    navigator.plugins !== undefined &&
    navigator.plugins.length === 0 &&
    !navigator.userAgent.includes("Mobile")
  ) return true;

  return false;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const InputField = ({ id, label, type = "text", value, onChange, autoComplete, disabled, placeholder }) => (
  <div className={styles.field}>
    <label htmlFor={id} className={styles.label}>{label}</label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      disabled={disabled}
      placeholder={placeholder}
      required
      aria-required="true"
      className={styles.input}
    />
  </div>
);

// ─── Icons ────────────────────────────────────────────────────────────────────

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

// ─── Lockout Banner ───────────────────────────────────────────────────────────

const LockoutBanner = ({ secondsLeft }) => (
  <div className={styles.lockoutBanner} role="alert" aria-live="assertive">
    <ShieldIcon />
    <span>
      Conta temporariamente bloqueada.{" "}
      <strong>Aguarde {formatCountdown(secondsLeft)}</strong> para tentar novamente.
    </span>
  </div>
);

// ─── Attempts Warning ─────────────────────────────────────────────────────────

const AttemptsWarning = ({ remaining }) => {
  if (remaining >= MAX_ATTEMPTS) return null;
  return (
    <p className={styles.attemptsWarning} role="status" aria-live="polite">
      {remaining === 1
        ? "⚠ Última tentativa antes do bloqueio."
        : `⚠ ${remaining} tentativas restantes antes do bloqueio.`}
    </p>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const Login = () => {
  const { login } = useContext(AuthContext);
  const navigate   = useNavigate();

  const [loginInput,   setLoginInput]   = useState("");
  const [senha,        setSenha]        = useState("");
  const [loading,      setLoading]      = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ─── Security state ──────────────────────────────────────────────────────
  const [lockoutSeconds,   setLockoutSeconds]   = useState(0);
  const [attemptsLeft,     setAttemptsLeft]     = useState(MAX_ATTEMPTS);
  // Honeypot — campo invisível; bots preenchem, humanos não veem
  const [honeypot,         setHoneypot]         = useState("");
  // Timing: registra quando o componente foi montado
  const mountTimeRef       = useRef(Date.now());
  // Rastreia interação humana (movimento de mouse, keydown)
  const hasInteractedRef   = useRef(false);
  const countdownRef       = useRef(null);

  // ─── Init: verifica lockout existente ────────────────────────────────────
  useEffect(() => {
    const lockout = getLockoutState();
    if (lockout) {
      const remaining = Math.ceil((lockout.until - Date.now()) / 1000);
      setLockoutSeconds(remaining);
    }
    setAttemptsLeft(MAX_ATTEMPTS - getAttempts());
  }, []);

  // ─── Countdown timer ─────────────────────────────────────────────────────
  useEffect(() => {
    if (lockoutSeconds <= 0) {
      clearInterval(countdownRef.current);
      return;
    }
    countdownRef.current = setInterval(() => {
      setLockoutSeconds((s) => {
        if (s <= 1) {
          clearInterval(countdownRef.current);
          setAttemptsLeft(MAX_ATTEMPTS - getAttempts());
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [lockoutSeconds]);

  // ─── Interação humana ────────────────────────────────────────────────────
  useEffect(() => {
    const markInteraction = () => { hasInteractedRef.current = true; };
    window.addEventListener("mousemove", markInteraction, { once: true });
    window.addEventListener("keydown",   markInteraction, { once: true });
    window.addEventListener("touchstart",markInteraction, { once: true });
    return () => {
      window.removeEventListener("mousemove", markInteraction);
      window.removeEventListener("keydown",   markInteraction);
      window.removeEventListener("touchstart",markInteraction);
    };
  }, []);

  // ─── Dark mode ───────────────────────────────────────────────────────────
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return document.documentElement.getAttribute("data-theme") === "dark";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  const toggleTheme = useCallback(() => setIsDark((v) => !v), []);

  // ─── Auth ─────────────────────────────────────────────────────────────────

  const persistSession = useCallback((usuario, token) => {
    localStorage.setItem("token",   token);
    localStorage.setItem("usuario", JSON.stringify(usuario));
  }, []);

  const redirectUser = useCallback(
    (nivel) => navigate(ROLE_ROUTES[nivel] ?? "/"),
    [navigate]
  );

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (loading) return;

    // ── 1. Lockout ativo ─────────────────────────────────────────────────
    const lockout = getLockoutState();
    if (lockout) {
      const remaining = Math.ceil((lockout.until - Date.now()) / 1000);
      setLockoutSeconds(remaining);
      showLockoutWarning(remaining);
      return;
    }

    // ── 2. Honeypot — campo invisível preenchido = bot ───────────────────
    if (honeypot) {
      // Falha silenciosa — não revela ao bot que foi detectado
      await new Promise((r) => setTimeout(r, 800));
      showLoginError("Não foi possível realizar o login. Tente novamente.");
      return;
    }

    // ── 3. Tempo mínimo no formulário ────────────────────────────────────
    const timeOnForm = Date.now() - mountTimeRef.current;
    if (timeOnForm < MIN_SUBMIT_MS) {
      await new Promise((r) => setTimeout(r, MIN_SUBMIT_MS - timeOnForm));
    }

    // ── 4. Detecção de automação ─────────────────────────────────────────
    if (detectAutomation()) {
      showLoginError("Acesso bloqueado. Por favor, utilize um navegador convencional.");
      return;
    }

    // ── 5. Interação humana ───────────────────────────────────────────────
    if (!hasInteractedRef.current) {
      showLoginError("Por favor, interaja com a página antes de enviar.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/usuarios/login", {
        login: loginInput.trim(),
        senha,
      });

      const { usuario, token } = data;

      // Sucesso: limpa tentativas
      clearAttempts();
      setAttemptsLeft(MAX_ATTEMPTS);

      login(usuario, token);
      persistSession(usuario, token);

      await showLoginSuccess();
      redirectUser(usuario.nivel_acesso);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        "Não foi possível realizar o login. Tente novamente.";

      // Registra falha e aplica lockout se necessário
      const result = recordFailedAttempt();

      if (result.locked) {
        setLockoutSeconds(result.duration);
        Swal.fire({
          icon: "error",
          title: "Conta bloqueada temporariamente",
          text: `Muitas tentativas. Aguarde ${formatCountdown(result.duration)} antes de tentar novamente.`,
        });
      } else {
        setAttemptsLeft(result.remaining);
        showLoginError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [loading, loginInput, senha, honeypot, login, persistSession, redirectUser]);

  const togglePassword = useCallback(() => setShowPassword((v) => !v), []);

  const isLocked         = lockoutSeconds > 0;
  const isSubmitDisabled = loading || !loginInput.trim() || !senha || isLocked;

  return (
    <main className={styles.page} aria-label="Página de login">
      <div className={styles.bgShape} aria-hidden="true" />

      {/* Dark mode toggle */}
      <button
        type="button"
        className={styles.themeToggle}
        onClick={toggleTheme}
        aria-label={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
      >
        {isDark ? <SunIcon /> : <MoonIcon />}
      </button>

      <div className={styles.card}>
        <div className={styles.logoWrapper}>
          <img src={logoBranca} alt="FluxoEasy" className={styles.logo} />
        </div>

        <h1 className={styles.title}>Bem-vindo de volta</h1>
        <p className={styles.subtitle}>Acesse o sistema FluxoEasy</p>

        {/* Banner de lockout */}
        {isLocked && <LockoutBanner secondsLeft={lockoutSeconds} />}

        {/* Aviso de tentativas restantes */}
        {!isLocked && <AttemptsWarning remaining={attemptsLeft} />}

        <form
          className={styles.form}
          onSubmit={handleSubmit}
          noValidate
          aria-label="Formulário de login"
        >
          {/*
            ── Honeypot ────────────────────────────────────────────────────────
            Visually hidden — screen readers/users never veem ou interagem.
            Bots preenchem automaticamente.
          */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
              opacity: 0,
              tabIndex: -1,
            }}
          >
            <label htmlFor="hp-email">Email</label>
            <input
              id="hp-email"
              type="text"
              name="email"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              autoComplete="off"
              tabIndex={-1}
            />
          </div>

          <InputField
            id="login-user"
            label="Usuário"
            value={loginInput}
            onChange={(e) => setLoginInput(e.target.value)}
            autoComplete="username"
            disabled={loading || isLocked}
            placeholder="Seu usuário"
          />

          <div className={styles.field}>
            <label htmlFor="login-senha" className={styles.label}>Senha</label>
            <div className={styles.passwordWrapper}>
              <input
                id="login-senha"
                type={showPassword ? "text" : "password"}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="current-password"
                disabled={loading || isLocked}
                placeholder="Sua senha"
                required
                aria-required="true"
                className={styles.input}
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={togglePassword}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                disabled={isLocked}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`${styles.submitBtn} ${isLocked ? styles.submitBtnLocked : ""}`}
            disabled={isSubmitDisabled}
            aria-busy={loading}
          >
            {isLocked ? (
              <><ShieldIcon /> Bloqueado — {formatCountdown(lockoutSeconds)}</>
            ) : loading ? (
              <><span className={styles.spinner} aria-hidden="true" /> Entrando...</>
            ) : (
              "Entrar"
            )}
          </button>
        </form>

        <p className={styles.footer}>
          FluxoEasy &copy; {new Date().getFullYear()}
        </p>
      </div>
    </main>
  );
};

export default Login;